/**
 * Phase 19 / Task 19.2 (DEC-325) — pure formatting helpers for the dashboards.
 * No React, so they are tested directly.
 */

import { formatCentsAsDollars } from '@lodex-pro/shared/money';
import type { ComplianceField } from './use-operations-dashboard';

/** Today's calendar date in `timeZone`, as 'YYYY-MM-DD' (never the UTC date, DEC-004). */
export function localToday(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (t: string): string => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Shift a 'YYYY-MM-DD' calendar date by whole days (date-only math, DST-safe). */
export function shiftDate(ymd: string, days: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (y === undefined || m === undefined || d === undefined) return ymd;
  const dt = new Date(Date.UTC(y, m - 1, d) + days * 86_400_000);
  return dt.toISOString().slice(0, 10);
}

export type RangePreset = 'TODAY' | 'LAST_7' | 'LAST_30';

/** Inclusive [from, to] for a preset, ending today in `timeZone`. */
export function presetRange(
  preset: RangePreset,
  timeZone: string,
  now: Date = new Date(),
): { from: string; to: string } {
  const to = localToday(timeZone, now);
  const back = preset === 'TODAY' ? 0 : preset === 'LAST_7' ? 6 : 29;
  return { from: shiftDate(to, -back), to };
}

/** 'YYYY-MM-DD' → "Oct 5, 2026" (a calendar date: formatted in UTC so it never shifts a day). */
export function formatDay(ymd: string): string {
  const t = Date.parse(`${ymd}T00:00:00Z`);
  if (Number.isNaN(t)) return ymd;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(t));
}

/** Seconds → "12.5 h" (one decimal). */
export function formatHours(secs: number): string {
  return `${(Math.round(secs / 360) / 10).toFixed(1)} h`;
}

/** '1234.50' → "1,234.50" without going through a float. */
export function formatBbl(bbl: string): string {
  const neg = bbl.startsWith('-');
  const [whole = '0', frac = '00'] = (neg ? bbl.slice(1) : bbl).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${neg ? '-' : ''}${grouped}.${frac.padEnd(2, '0').slice(0, 2)}`;
}

/** 87.5 → "87.5%"; null → "—". */
export function formatPct(pct: number | null): string {
  return pct === null ? '—' : `${pct.toFixed(1)}%`;
}

const FIELD_LABEL: Record<ComplianceField, string> = {
  CDL: 'CDL',
  TWIC: 'TWIC card',
  HAZMAT: 'Hazmat endorsement',
  DOT_MEDICAL: 'DOT medical card',
  REGISTRATION: 'Registration',
};

export function complianceFieldLabel(field: ComplianceField): string {
  return FIELD_LABEL[field];
}

/** -5 → "Expired 5 days ago"; 0 → "Expires today"; 1 → "Expires tomorrow"; 10 → "Expires in 10 days". */
export function daysRemainingLabel(days: number): string {
  if (days < -1) return `Expired ${String(-days)} days ago`;
  if (days === -1) return 'Expired yesterday';
  if (days === 0) return 'Expires today';
  if (days === 1) return 'Expires tomorrow';
  return `Expires in ${String(days)} days`;
}

/** Integer cents → "$12,345.67" (shared money.ts does the cents math; this only groups). */
export function formatMoney(cents: number): string {
  const raw = formatCentsAsDollars(cents);
  const neg = raw.startsWith('-');
  return `${neg ? '-' : ''}$${formatBbl(neg ? raw.slice(1) : raw)}`;
}

/** % change → an arrow, a sign and a word-free label; never colour alone. */
export function formatChange(pct: number | null): {
  readonly text: string;
  readonly direction: 'UP' | 'DOWN' | 'FLAT' | 'NONE';
} {
  if (pct === null) return { text: 'no prior data', direction: 'NONE' };
  if (pct === 0) return { text: '0.0%', direction: 'FLAT' };
  return pct > 0
    ? { text: `▲ ${pct.toFixed(1)}%`, direction: 'UP' }
    : { text: `▼ ${Math.abs(pct).toFixed(1)}%`, direction: 'DOWN' };
}

/** "Sep 1 – Sep 7, 2026" for an inclusive range. */
export function formatRange(from: string, to: string): string {
  if (from === to) return formatDay(from);
  const short = (ymd: string): string =>
    new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
      new Date(Date.parse(`${ymd}T00:00:00Z`)),
    );
  return `${short(from)} – ${formatDay(to)}`;
}
